// Library imports
import React from 'react';
import { MDBModal, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter, MDBBtn, MDBSpinner } from 'mdb-react-ui-kit';
import moment from 'moment';
import { bool, func, object } from 'prop-types';
// Styled components
import {
    ModalBodyWrapper,
    LoadingContainer,
    NoDataContainer,
    GameHeader,
    HeaderContent,
    TeamsContainer,
    TeamInfo,
    TeamAbbreviation,
    TeamFullName,
    ScoreContainer,
    ScoreBox,
    ScoreDivider,
    DateTime,
    BackgroundPattern,
    GameStatsWrapper,
    Section,
    SectionHeader,
    PitchersGrid,
    PitcherCard,
    PitcherHeader,
    PitcherName,
    PitcherTeam,
    PitcherStats,
    PitcherStat,
    PitcherStatValue,
    PitcherStatLabel,
    GameId,
    BettingSubsection,
    BettingSubheader,
    BookList,
    BookItem,
    BookInfo,
    BookLogo,
    BookLabel,
    OddsContainer,
    OddsSection,
    TeamAbbrev,
    MoneylineOdds,
    SpreadOdds,
} from './styles';
import { GameDetailsModalDialog } from '../../styles';
import { formatShortDate } from '../../../utils';

const PitcherHistoryModal = ({ isOpen, onClose, gameData, loading }) => {
    const renderLoadingState = () => (
        <LoadingContainer>
            <MDBSpinner />
            <p className="mt-2">Loading game details...</p>
        </LoadingContainer>
    );

    const renderNoDataState = () => (
        <NoDataContainer>
            <p>No game data available</p>
        </NoDataContainer>
    );

    const renderGameHeader = () => (
        <GameHeader $gameData={gameData}>
            <HeaderContent>
                <TeamsContainer>
                    <TeamInfo>
                        <TeamAbbreviation>{gameData.awayTeam?.abbr || gameData.awayTeam?.teamName}</TeamAbbreviation>
                        <TeamFullName>{gameData.awayTeam?.teamName}</TeamFullName>
                    </TeamInfo>

                    <ScoreContainer>
                        <ScoreBox $isWinner={gameData.winner === gameData.awayTeam?.teamId} $isAway={true}>
                            {gameData.awayTeam?.score || 0}
                        </ScoreBox>
                        <ScoreDivider>-</ScoreDivider>
                        <ScoreBox $isWinner={gameData.winner === gameData.homeTeam?.teamId} $isAway={false}>
                            {gameData.homeTeam?.score || 0}
                        </ScoreBox>
                    </ScoreContainer>

                    <TeamInfo>
                        <TeamAbbreviation>{gameData.homeTeam?.abbr || gameData.homeTeam?.teamName}</TeamAbbreviation>
                        <TeamFullName>{gameData.homeTeam?.teamName}</TeamFullName>
                    </TeamInfo>
                </TeamsContainer>

                <DateTime>{moment(gameData.dateTime).format('MMMM Do, YYYY [at] h:mm A')}</DateTime>
            </HeaderContent>

            <BackgroundPattern />
        </GameHeader>
    );

    const renderPitchersSection = () => {
        if (!gameData.awayTeam?.pitcher && !gameData.homeTeam?.pitcher) return null;

        return (
            <Section>
                <SectionHeader>⚾ Starting Pitchers</SectionHeader>
                <PitchersGrid>
                    {[
                        { team: gameData.awayTeam, label: 'Away' },
                        { team: gameData.homeTeam, label: 'Home' },
                    ]
                        .filter(({ team }) => team?.pitcher)
                        .map(({ team, label }) => (
                            <PitcherCard key={label}>
                                <PitcherHeader>
                                    <div>
                                        <PitcherName>
                                            {team.pitcher.firstName} {team.pitcher.lastName}
                                        </PitcherName>
                                        <PitcherTeam>
                                            {team.abbr || team.teamName} - {team.pitcher.throws ? `${team.pitcher.throws} Handed` : 'Unknown'}
                                        </PitcherTeam>
                                    </div>
                                </PitcherHeader>
                                <PitcherStats>
                                    <PitcherStat>
                                        <PitcherStatValue>{team.pitcher.stats?.['Earned Run Average'] ?? 'N/A'}</PitcherStatValue>
                                        <PitcherStatLabel>ERA</PitcherStatLabel>
                                    </PitcherStat>
                                    <PitcherStat>
                                        <PitcherStatValue>{team.pitcher.stats?.['Walks Plus Hits Per Inning Pitched'] ?? 'N/A'}</PitcherStatValue>
                                        <PitcherStatLabel>WHIP</PitcherStatLabel>
                                    </PitcherStat>
                                    <PitcherStat>
                                        <PitcherStatValue>{team.pitcher.stats?.['Wins-Losses'] ?? 'N/A'}</PitcherStatValue>
                                        <PitcherStatLabel>W-L</PitcherStatLabel>
                                    </PitcherStat>
                                </PitcherStats>
                            </PitcherCard>
                        ))}
                </PitchersGrid>
            </Section>
        );
    };

    const renderBettingLines = () => {
        if (!gameData.bookData) return null;

        return (
            <Section>
                <SectionHeader>💰 Betting Lines</SectionHeader>

                {/* Moneylines */}
                {gameData.bookData.moneylines && gameData.bookData.moneylines.length > 0 && (
                    <BettingSubsection>
                        <BettingSubheader>Moneylines</BettingSubheader>
                        <BookList>
                            {gameData.bookData.moneylines.map((book, index) => {
                                return (
                                    <BookItem key={index}>
                                        <BookInfo>
                                            <BookLogo src={book.logo} alt={book.label} $bookColor={book.color} />
                                            <BookLabel>{book.label}</BookLabel>
                                        </BookInfo>
                                        <OddsContainer>
                                            <OddsSection>
                                                <TeamAbbrev>{gameData.awayTeam?.abbr}</TeamAbbrev>
                                                <MoneylineOdds $line={book.awayLine}>
                                                    {book.awayLine > 0 ? '+' : ''}
                                                    {book.awayLine}
                                                </MoneylineOdds>
                                            </OddsSection>
                                            <OddsSection>
                                                <TeamAbbrev>{gameData.homeTeam?.abbr}</TeamAbbrev>
                                                <MoneylineOdds $line={book.homeLine}>
                                                    {book.homeLine > 0 ? '+' : ''}
                                                    {book.homeLine}
                                                </MoneylineOdds>
                                            </OddsSection>
                                        </OddsContainer>
                                    </BookItem>
                                );
                            })}
                        </BookList>
                    </BettingSubsection>
                )}

                {/* Spreads */}
                {gameData.bookData.spreads && gameData.bookData.spreads.length > 0 && (
                    <BettingSubsection>
                        <BettingSubheader>Spreads</BettingSubheader>
                        <BookList>
                            {gameData.bookData.spreads.map((book, index) => (
                                <BookItem key={index}>
                                    <BookInfo>
                                        <BookLogo src={book.logo} alt={book.label} $bookColor={book.color} />
                                        <BookLabel>{book.label}</BookLabel>
                                    </BookInfo>
                                    <OddsContainer>
                                        <OddsSection>
                                            <TeamAbbrev>{gameData.awayTeam?.abbr}</TeamAbbrev>
                                            <SpreadOdds>
                                                {book.awayLine > 0 ? '+' : ''}
                                                {book.awayLine}
                                            </SpreadOdds>
                                        </OddsSection>
                                        <OddsSection>
                                            <TeamAbbrev>{gameData.homeTeam?.abbr}</TeamAbbrev>
                                            <SpreadOdds>
                                                {book.homeLine > 0 ? '+' : ''}
                                                {book.homeLine}
                                            </SpreadOdds>
                                        </OddsSection>
                                    </OddsContainer>
                                </BookItem>
                            ))}
                        </BookList>
                    </BettingSubsection>
                )}
            </Section>
        );
    };

    const renderGameContent = () => {
        if (loading) return renderLoadingState();
        if (!gameData) return renderNoDataState();

        return (
            <div>
                {renderGameHeader()}
                <GameStatsWrapper>
                    {renderPitchersSection()}
                    {renderBettingLines()}
                    {gameData.gameId && (
                        <Section>
                            <GameId>Game ID: {gameData.gameId}</GameId>
                        </Section>
                    )}
                </GameStatsWrapper>
            </div>
        );
    };

    return (
        <MDBModal open={isOpen} onClose={onClose} tabIndex="-1">
            <GameDetailsModalDialog size="lg" centered scrollable>
                <MDBModalContent>
                    <MDBModalHeader>
                        <MDBModalTitle>Game Details - {gameData && formatShortDate(gameData.dateTime)}</MDBModalTitle>
                        <MDBBtn className="btn-close" color="none" onClick={onClose} aria-label="Close" />
                    </MDBModalHeader>
                    <MDBModalBody>
                        <ModalBodyWrapper>{renderGameContent()}</ModalBodyWrapper>
                    </MDBModalBody>
                    <MDBModalFooter>
                        <MDBBtn color="secondary" onClick={onClose}>
                            Close
                        </MDBBtn>
                    </MDBModalFooter>
                </MDBModalContent>
            </GameDetailsModalDialog>
        </MDBModal>
    );
};

PitcherHistoryModal.propTypes = {
    isOpen: bool.isRequired,
    onClose: func.isRequired,
    gameData: object,
    loading: bool,
};

PitcherHistoryModal.defaultProps = {
    gameData: null,
    loading: false,
};

export default PitcherHistoryModal;
